import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Keyboard,
  ViewportFit,
} from "@eweadn-qiankun/shared-components";
import type {
  CommunityPreviewDeviceMeta,
  FullBoardConfigData,
} from "@eweadn-qiankun/shared-types";
import {
  buildPreviewKeyboardLayout,
  buildPreviewKeyboardLayoutWithChangeKey,
  resolveCommunityPreviewKeyboardType,
} from "../utils/buildPreviewKeyboardLayout";
import {
  getChangeKeyLayerOptions,
  getChangeKeySelectedUuids,
  resolveDk108KnobBindingsFromConfig,
} from "../utils/changeKeyPreviewUtils";
import { isHfdDk108KnobKeyboard } from "@eweadn-qiankun/shared-utils";
import Dk108KnobRemapPreview from "./Dk108KnobRemapPreview";
import "../../../../../keyboard-management/src/pages/changeKey/index.scss";

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: FullBoardConfigData;
  /** 0=Win，1=Mac，星闪仅展示当前系统层 */
  currentDeviceSystem?: number;
}

function resolveDefaultLayerId(
  currentDeviceSystem: number,
  layerOptions: ReturnType<typeof getChangeKeyLayerOptions>
): string {
  const preferred = currentDeviceSystem === 1 ? "MAC-0" : "WIN-0";
  return (
    layerOptions.find((item) => item.id === preferred)?.id ??
    layerOptions[0]?.id ??
    "WIN-0"
  );
}

export default function KeyboardChangeKeySection({
  meta,
  configData,
  currentDeviceSystem = 0,
}: Props) {
  const { t } = useTranslation();
  const previewKeyboardType = useMemo(
    () => resolveCommunityPreviewKeyboardType(meta),
    [meta]
  );
  const baseLayout = useMemo(() => buildPreviewKeyboardLayout(meta), [meta]);

  const layerOptions = useMemo(
    () =>
      getChangeKeyLayerOptions(undefined, {
        solutionProvider: meta.solutionProvider,
        currentDeviceSystem,
      }),
    [meta.solutionProvider, currentDeviceSystem]
  );

  const [activeLayerId, setActiveLayerId] = useState(() =>
    resolveDefaultLayerId(currentDeviceSystem, layerOptions)
  );

  // 仅在 Win/Mac 系统切换时回到默认层；勿依赖 layerOptions 引用，避免父组件重渲染把 Fn 层打回 Base
  useEffect(() => {
    setActiveLayerId(resolveDefaultLayerId(currentDeviceSystem, layerOptions));
  }, [currentDeviceSystem, meta.solutionProvider, layerOptions]);

  const activeLayer = useMemo(
    () =>
      layerOptions.find((item) => item.id === activeLayerId) ?? layerOptions[0],
    [layerOptions, activeLayerId]
  );

  const layout = useMemo(() => {
    if (!activeLayer) return buildPreviewKeyboardLayout(meta);
    return buildPreviewKeyboardLayoutWithChangeKey(
      meta,
      configData,
      currentDeviceSystem,
      { system: activeLayer.system, layer: activeLayer.layer }
    );
  }, [meta, configData, activeLayer, currentDeviceSystem]);

  const showDk108KnobDemo =
    meta.solutionProvider === "huafenda" &&
    isHfdDk108KnobKeyboard(previewKeyboardType);
  const dk108KnobBindings = useMemo(
    () => resolveDk108KnobBindingsFromConfig(configData),
    [configData]
  );

  const remappedKeyUuids = useMemo(() => {
    if (!activeLayer) return [];
    return getChangeKeySelectedUuids(
      layout,
      configData,
      activeLayer.system,
      activeLayer.layer,
      meta.solutionProvider,
      previewKeyboardType,
      baseLayout
    );
  }, [
    layout,
    baseLayout,
    configData,
    activeLayer,
    meta.solutionProvider,
    previewKeyboardType,
  ]);

  return (
    <div className="change-key-page">
      <div className="layout-content">
        <ViewportFit>
          <div className="keyboard-with-layers">
            <div className="keyboard-display">
              <Keyboard
                key={activeLayerId}
                keyboardType={previewKeyboardType as never}
                layout={layout}
                styleMode="neutral"
                selectionMode="single"
                selectedKeys={remappedKeyUuids}
                showLabels
                enablePhysicalKeyboard={false}
                disableViewportFit
                allowIconKeySelectedStyle
                disabled
              />
            </div>

            {showDk108KnobDemo || layerOptions.length > 1 ? (
              <div className="keyboard-sidebar">
                {showDk108KnobDemo ? (
                  <Dk108KnobRemapPreview bindings={dk108KnobBindings} />
                ) : null}

                {layerOptions.length > 1 ? (
                  <div className="layer-switcher community-preview-interactive">
                    {layerOptions.map((option) => (
                      <Button
                        key={option.id}
                        className={`layer-btn ${
                          option.id === activeLayerId ? "active" : ""
                        }`}
                        onClick={() => setActiveLayerId(option.id)}
                      >
                        {t(option.labelKey)}
                      </Button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </ViewportFit>
      </div>
    </div>
  );
}
